import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

// Ligar pro contato pelo ramal do agente (Linkus / PABX Yeastar). O PABX toca o Linkus do agente e, quando ele
// atende, disca pro contato.

export interface LinkusAvailability { available: boolean; reason: string | null; extension: string | null }
export interface LinkusCallResult { ok: boolean; problem: string | null; number?: string | null; extension?: string | null; callId?: string | null }

export async function getLinkusAvailability(token: string): Promise<LinkusAvailability> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/linkus/disponibilidade`, { headers: wootdashAuthHeaders(token) })
    if (!res.ok) return { available: false, reason: 'Não deu pra verificar o Linkus agora.', extension: null }
    return await res.json()
  } catch {
    return { available: false, reason: 'Não deu pra verificar o Linkus agora.', extension: null }
  }
}

export async function startLinkusCall(token: string, conversationId: string): Promise<LinkusCallResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/linkus/ligar`, {
      method: 'POST',
      headers: { ...wootdashAuthHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId }),
    })
    if (res.status === 403) return { ok: false, problem: 'Você não tem permissão pra ligar (messages.send).' }
    if (!res.ok) return { ok: false, problem: `Erro ${res.status} no servidor.` }
    return await res.json()
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.' }
  }
}

// ── Telefone no navegador (Yeastar WebRTC SDK) ──

export interface PhoneCredentials { ok: boolean; problem: string | null; pbxUrl?: string | null; extension?: string | null; secret?: string | null }
export interface ResolvedNumber { ok: boolean; problem: string | null; number?: string | null; contactName?: string | null }
export interface CallerInfo { found: boolean; contactId?: string | null; contactName?: string | null; conversationId?: string | null }

/** Assinatura do ramal pra entrar no PABX pelo navegador (gerada no servidor). */
export async function getPhoneCredentials(token: string): Promise<PhoneCredentials> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/linkus/telefone/credenciais`, { method: 'POST', headers: wootdashAuthHeaders(token) })
    if (!res.ok) return { ok: false, problem: res.status === 403 ? 'Sem permissão pra usar o telefone.' : `Erro ${res.status} no servidor.` }
    return await res.json()
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.' }
  }
}

/** Número do contato da conversa já no formato do PABX. */
export async function resolveCallNumber(token: string, conversationId: string): Promise<ResolvedNumber> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/linkus/numero?conversaId=${encodeURIComponent(conversationId)}`, { headers: wootdashAuthHeaders(token) })
    if (!res.ok) return { ok: false, problem: `Erro ${res.status} no servidor.` }
    return await res.json()
  } catch {
    return { ok: false, problem: 'Não foi possível falar com o servidor.' }
  }
}

/** Quem está ligando (contato + conversa mais recente), pelo número que o PABX mostra. */
export async function identifyCaller(token: string, number: string): Promise<CallerInfo> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/linkus/quem-liga?numero=${encodeURIComponent(number)}`, { headers: wootdashAuthHeaders(token) })
    return res.ok ? await res.json() : { found: false }
  } catch {
    return { found: false }
  }
}

// ── Histórico de ligações ──

export type CallOutcome = 'Answered' | 'NoAnswer' | 'Missed' | 'Rejected' | 'Failed' | 'Requested'
export interface CallSummary {
  id: string; number: string; direction: 'Outbound' | 'Inbound'; source: 'Browser' | 'Linkus'; outcome: CallOutcome
  startedAt: string; talkSeconds: number | null; contactId: string | null; contactName: string | null; conversationId: string | null
}
export interface RecordCallInput {
  clientCallId: string; number: string; direction: 'Outbound' | 'Inbound'; outcome: CallOutcome
  startedAt: string; answeredAt: string | null; endedAt: string | null; conversationId: string | null
}

/** Ligação do telefone do navegador terminou: o servidor guarda e põe a nota na conversa. */
export async function recordCall(token: string, input: RecordCallInput): Promise<void> {
  try {
    await fetch(`${WOOTDASH_BASE}/linkus/ligacoes`, {
      method: 'POST', headers: { ...wootdashAuthHeaders(token), 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    })
  } catch { /* sem rede: a ligação aconteceu, só não fica no histórico */ }
}

export async function listMyCalls(token: string): Promise<CallSummary[]> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/linkus/ligacoes/minhas`, { headers: wootdashAuthHeaders(token) })
    return res.ok ? await res.json() : []
  } catch {
    return []
  }
}
